import type { Workflow, Agent, Team, Trigger } from "@/stores/WorkflowStore";

import { type Node, type Edge, MarkerType, Position } from "@xyflow/react";
import dagre from "@dagrejs/dagre";

// ─── Node data interfaces ────────────────────────────────────────────────────

export interface PlaygroundNodeData extends Record<string, unknown> {
  label: string;
  agentId?: string;
  agentType?: string;
  parameters?: Record<string, any>;
  description?: string;
  toolId?: string;
  toolIcon?: string;
  teamStrategy?: "selector" | "mixture";
  teamGoal?: string;
  teamMembers?: string[];
  teamMaxCycles?: number;
  teamModel?: string;
  teamUseLlmJudgment?: boolean;
  teamSelectorPrompt?: string | null;
  teamAggregatorPrompt?: string | null;
  inputParams?: Record<string, any>;
  // Execution state (used in Phase 3)
  status?: "idle" | "running" | "success" | "error";
  isCurrentStep?: boolean;
  // Validation errors for this node
  validationErrors?: string[];
}

export type PlaygroundNode = Node<PlaygroundNodeData>;
export interface PlaygroundEdgeData extends Record<string, unknown> {
  mapping?: Record<string, string>;
  edgeType?: "trigger" | "tool" | "subteam" | "linear" | "final";
}

export type PlaygroundEdge = Edge<PlaygroundEdgeData>;

// ─── Layout constants ────────────────────────────────────────────────────────

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const TEAM_NODE_WIDTH = 240;
const TEAM_NODE_HEIGHT = 100;

// ─── Dagre auto-layout ──────────────────────────────────────────────────────

export const getLayoutedElements = (
  nodes: PlaygroundNode[],
  edges: PlaygroundEdge[],
  direction: "TB" | "LR" = "LR",
): { nodes: PlaygroundNode[]; edges: PlaygroundEdge[] } => {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  const isHorizontal = direction === "LR";

  dagreGraph.setGraph({ rankdir: direction, nodesep: 60, ranksep: 80 });

  nodes.forEach((node) => {
    const width = node.type === "team" ? TEAM_NODE_WIDTH : NODE_WIDTH;
    const height = node.type === "team" ? TEAM_NODE_HEIGHT : NODE_HEIGHT;

    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const width = node.type === "team" ? TEAM_NODE_WIDTH : NODE_WIDTH;
    const height = node.type === "team" ? TEAM_NODE_HEIGHT : NODE_HEIGHT;

    return {
      ...node,
      sourcePosition: (isHorizontal ? Position.Right : Position.Bottom) as Position,
      targetPosition: (isHorizontal ? Position.Left : Position.Top) as Position,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// ─── Workflow → Nodes conversion ─────────────────────────────────────────────

/**
 * Converts a Workflow object into ReactFlow nodes.
 * Handles agents, teams, tools, and input/output nodes.
 */
export const workflowToNodes = (workflow: Workflow): PlaygroundNode[] => {
  const nodes: PlaygroundNode[] = [];

  // Collect team member IDs (agents that belong to teams, not sub-teams)
  const teamIds = new Set<string>(workflow.teams ? Object.keys(workflow.teams) : []);
  const teamMemberIds = new Set<string>();

  if (workflow.teams) {
    Object.values(workflow.teams).forEach((team: Team) => {
      team.members.forEach((memberId) => {
        if (!teamIds.has(memberId)) {
          teamMemberIds.add(memberId);
        }
      });
    });
  }

  const hasAgents = Object.keys(workflow.agents).length > 0;
  const hasTeams = teamIds.size > 0;

  // Only add input/output nodes when there is actual content
  if (!hasAgents && !hasTeams) {
    return [];
  }

  // Input node
  nodes.push({
    id: "input",
    type: "input",
    position: { x: 0, y: 0 },
    data: {
      label: "Input",
      inputParams: workflow.input || {},
    },
  });

  // Agent nodes (exclude team members — they appear inside team nodes)
  Object.entries(workflow.agents).forEach(([agentId, agent]: [string, Agent]) => {
    if (teamMemberIds.has(agentId)) return;

    nodes.push({
      id: agentId,
      type: "agent",
      position: { x: 0, y: 0 },
      data: {
        label: agentId,
        agentId,
        agentType: agent.type,
        parameters: agent.parameters,
        description: agent.description || undefined,
      },
    });

    // Tool nodes for this agent
    const tools = (agent.parameters?.tools as string[]) || [];

    tools.forEach((toolId: string) => {
      const toolNodeId = `${agentId}_tool_${toolId}`;

      // Avoid duplicates if multiple agents share the same tool
      if (!nodes.find((n) => n.id === toolNodeId)) {
        nodes.push({
          id: toolNodeId,
          type: "tool",
          position: { x: 0, y: 0 },
          data: {
            label: toolId
              .replace(/([a-z])([A-Z])/g, "$1 $2")
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char: string) => char.toUpperCase()),
            toolId,
          },
        });
      }
    });
  });

  // Team nodes
  if (workflow.teams) {
    Object.entries(workflow.teams).forEach(([teamId, team]: [string, Team]) => {
      nodes.push({
        id: teamId,
        type: "team",
        position: { x: 0, y: 0 },
        data: {
          label: teamId,
          teamStrategy: team.strategy,
          teamGoal: team.goal,
          teamMembers: team.members,
          teamMaxCycles: team.maxCycles,
          teamModel: team.model,
          teamUseLlmJudgment: team.useLlmJudgment,
          teamSelectorPrompt: team.selectorPrompt,
          teamAggregatorPrompt: team.aggregatorPrompt,
        },
      });
    });
  }

  // Output node
  nodes.push({
    id: "output",
    type: "output",
    position: { x: 0, y: 0 },
    data: {
      label: "Output",
    },
  });

  return nodes;
};

// ─── Workflow → Edges conversion ─────────────────────────────────────────────

/**
 * Converts a Workflow object into ReactFlow edges based on triggers.
 */
export const workflowToEdges = (workflow: Workflow): PlaygroundEdge[] => {
  const edges: PlaygroundEdge[] = [];
  const teamIds = new Set<string>(workflow.teams ? Object.keys(workflow.teams) : []);
  const teamMemberIds = new Set<string>();

  const hasAgents = Object.keys(workflow.agents).length > 0;
  const hasTeams = teamIds.size > 0;

  // Empty workflow → no edges
  if (!hasAgents && !hasTeams) {
    return [];
  }

  if (workflow.teams) {
    Object.values(workflow.teams).forEach((team: Team) => {
      team.members.forEach((memberId) => {
        if (!teamIds.has(memberId)) {
          teamMemberIds.add(memberId);
        }
      });
    });
  }

  const hasTriggers = Object.values(workflow.agents).some(
    (agent) => agent.triggers && agent.triggers.length > 0,
  );

  const hasTeamTriggers = workflow.teams
    ? Object.values(workflow.teams).some((team) => team.triggers && team.triggers.length > 0)
    : false;

  if (hasTriggers || hasTeamTriggers) {
    // Agent trigger edges (non-team-member agents)
    Object.entries(workflow.agents)
      .filter(([agentId]) => !teamMemberIds.has(agentId))
      .forEach(([agentId, agent]) => {
        (agent.triggers || []).forEach((trigger: Trigger) => {
          edges.push({
            id: `trigger-${trigger.source}-${agentId}`,
            source: trigger.source,
            target: agentId,
            type: "trigger",
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            data: {
              mapping: trigger.mapping || {},
              edgeType: "trigger" as const,
            },
          });
        });
      });

    // Team trigger edges
    if (workflow.teams) {
      Object.entries(workflow.teams).forEach(([teamId, team]: [string, Team]) => {
        (team.triggers || []).forEach((trigger: Trigger) => {
          edges.push({
            id: `trigger-${trigger.source}-${teamId}`,
            source: trigger.source,
            target: teamId,
            type: "trigger",
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            data: {
              mapping: trigger.mapping || {},
              edgeType: "trigger" as const,
            },
          });
        });

        // Sub-team edges (parent team → sub-team)
        team.members
          .filter((memberId) => teamIds.has(memberId))
          .forEach((subTeamId) => {
            edges.push({
              id: `subteam-${teamId}-${subTeamId}`,
              source: teamId,
              target: subTeamId,
              type: "trigger",
              animated: true,
              style: { strokeDasharray: "5,5" },
              markerEnd: { type: MarkerType.ArrowClosed },
              data: { edgeType: "subteam" as const },
            });
          });
      });
    }

    // Connect final nodes (no outgoing triggers) to output
    const nodesWithOutgoing = new Set<string>();

    Object.values(workflow.agents).forEach((agent) => {
      (agent.triggers || []).forEach((trigger) => {
        nodesWithOutgoing.add(trigger.source);
      });
    });
    if (workflow.teams) {
      Object.values(workflow.teams).forEach((team) => {
        (team.triggers || []).forEach((trigger) => {
          nodesWithOutgoing.add(trigger.source);
        });
      });
    }

    const allNodeIds = [
      ...Object.keys(workflow.agents).filter((id) => !teamMemberIds.has(id)),
      ...Object.keys(workflow.teams || {}),
    ];
    const finalNodes = allNodeIds.filter((id) => !nodesWithOutgoing.has(id));

    (finalNodes.length > 0 ? finalNodes : allNodeIds).forEach((nodeId) => {
      edges.push({
        id: `final-${nodeId}-output`,
        source: nodeId,
        target: "output",
        type: "default",
        markerEnd: { type: MarkerType.ArrowClosed },
      });
    });
  } else {
    // Fallback: linear flow if no triggers defined
    const agentIds = Object.keys(workflow.agents).filter((id) => !teamMemberIds.has(id));
    const teamNodeIds = Object.keys(workflow.teams || {});
    const allIds = [...agentIds, ...teamNodeIds];

    if (allIds.length > 0) {
      edges.push({
        id: `linear-input-${allIds[0]}`,
        source: "input",
        target: allIds[0],
        type: "default",
        markerEnd: { type: MarkerType.ArrowClosed },
      });

      for (let i = 0; i < allIds.length - 1; i++) {
        edges.push({
          id: `linear-${allIds[i]}-${allIds[i + 1]}`,
          source: allIds[i],
          target: allIds[i + 1],
          type: "default",
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      }

      edges.push({
        id: `linear-${allIds[allIds.length - 1]}-output`,
        source: allIds[allIds.length - 1],
        target: "output",
        type: "default",
        markerEnd: { type: MarkerType.ArrowClosed },
      });
    } else {
      edges.push({
        id: "linear-input-output",
        source: "input",
        target: "output",
        type: "default",
        markerEnd: { type: MarkerType.ArrowClosed },
      });
    }
  }

  // Tool → Agent edges (non-team-member agents only)
  Object.entries(workflow.agents)
    .filter(([agentId]) => !teamMemberIds.has(agentId))
    .forEach(([agentId, agent]) => {
      const tools = (agent.parameters?.tools as string[]) || [];

      tools.forEach((toolId: string) => {
        const toolNodeId = `${agentId}_tool_${toolId}`;

        edges.push({
          id: `tool-${toolNodeId}-${agentId}`,
          source: toolNodeId,
          target: agentId,
          type: "default",
          style: { strokeDasharray: "3,3", opacity: 0.6 },
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      });
    });

  return edges;
};

// ─── Combined helper ─────────────────────────────────────────────────────────

/**
 * Converts a Workflow into layouted ReactFlow nodes and edges.
 */
export const workflowToLayoutedElements = (
  workflow: Workflow,
  direction: "TB" | "LR" = "LR",
): { nodes: PlaygroundNode[]; edges: PlaygroundEdge[] } => {
  const nodes = workflowToNodes(workflow);
  const edges = workflowToEdges(workflow);

  return getLayoutedElements(nodes, edges, direction);
};
